/**
 * Style Categorization Helper
 * Maps individual styles to their parent categories
 */
if (typeof StyleCategorizer === 'undefined') {
class StyleCategorizer {
  constructor() {
    this.styleCategories = null;
    this.styleMap = new Map();
    this.loadStyleCategories();
  }

  async loadStyleCategories() {
    try {
      const response = await fetch(window.styleCategoriesUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      this.styleCategories = await response.json();
      this.buildStyleMap();
    } catch (error) {
      console.error('Failed to load style categories:', error);
      // Fallback to basic categorization
      this.styleCategories = {};
    }
  }

  buildStyleMap() {
    if (!this.styleCategories) return;

    // Build a reverse map from style to category
    Object.entries(this.styleCategories).forEach(([category, styles]) => {
      styles.forEach(style => {
        this.styleMap.set(style.toLowerCase(), category);
      });
    });
  }

  getCategory(style) {
    if (!style || !this.styleCategories) {
      return 'Other';
    }

    const cleanStyle = style.trim();
    
    // Direct match
    const directMatch = this.styleMap.get(cleanStyle.toLowerCase());
    if (directMatch) {
      return directMatch;
    }

    // Partial match - check if style contains any known style
    for (const [knownStyle, category] of this.styleMap.entries()) {
      if (cleanStyle.toLowerCase().includes(knownStyle)) {
        return category;
      }
    }

    // Keyword-based fallback
    const styleLower = cleanStyle.toLowerCase();
    
    // 19th Century keywords
    if (styleLower.includes('empire') || styleLower.includes('victorian') || styleLower.includes('regency')) {
      return '19th Century Styles';
    }
    if (styleLower.includes('georgian') || styleLower.includes('federal') || styleLower.includes('biedermeier')) {
      return '19th Century Styles';
    }
    
    // Early 20th Century keywords
    if (styleLower.includes('art nouveau') || styleLower.includes('arts and crafts')) {
      return 'Early 20th Century Movements';
    }
    if (styleLower.includes('bauhaus') || styleLower.includes('art deco') || styleLower.includes('streamline')) {
      return 'Early 20th Century Movements';
    }
    
    // Mid-Century keywords
    if (styleLower.includes('mid-century') || styleLower.includes('midcentury')) {
      return 'Mid-20th Century';
    }
    if (styleLower.includes('scandinavian') || styleLower.includes('organic modern')) {
      return 'Mid-20th Century';
    }
    
    // Late 20th Century keywords
    if (styleLower.includes('brutalism') || styleLower.includes('postmodern')) {
      return 'Late 20th Century';
    }
    if (styleLower.includes('memphis') || styleLower.includes('high-tech') || styleLower.includes('space age')) {
      return 'Late 20th Century';
    }
    
    // Contemporary keywords
    if (styleLower.includes('minimalism') || styleLower.includes('minimalist')) {
      return 'Contemporary & Transitional';
    }
    if (styleLower.includes('industrial') || styleLower.includes('contemporary')) {
      return 'Contemporary & Transitional';
    }
    if (styleLower.includes('transitional') || styleLower.includes('modern rustic')) {
      return 'Contemporary & Transitional';
    }
    
    // Asian & Nature keywords
    if (styleLower.includes('japandi') || styleLower.includes('wabi-sabi')) {
      return 'Asian & Nature Inspired';
    }
    if (styleLower.includes('biophilic') || styleLower.includes('sustainable') || styleLower.includes('eco')) {
      return 'Asian & Nature Inspired';
    }
    
    // Revival keywords
    if (styleLower.includes('eclectic') || styleLower.includes('revival')) {
      return 'Revival & Eclectic Styles';
    }
    if (styleLower.includes('neo-classic') || styleLower.includes('colonial')) {
      return 'Revival & Eclectic Styles';
    }
    
    // 21st Century keywords
    if (styleLower.includes('coastal') || styleLower.includes('farmhouse')) {
      return '21st Century & Emerging';
    }
    if (styleLower.includes('bohemian') || styleLower.includes('retro')) {
      return '21st Century & Emerging';
    }
    if (styleLower.includes('y2k') || styleLower.includes('digital') || styleLower.includes('parametric')) {
      return '21st Century & Emerging';
    }

    return 'Other';
  }

  getAllCategories() {
    return Object.keys(this.styleCategories || {});
  }

  getStylesInCategory(category) {
    return this.styleCategories?.[category] || [];
  }
}

// Global instance
if (!window.styleCategorizer) {
  window.styleCategorizer = new StyleCategorizer();
}
}
